import type { ReactNode } from "react";

/** A list of rows with a consistent layout: title and details on the left, a value and actions on the right. */
export function List(props: { children: ReactNode; className?: string; "aria-label"?: string }) {
  return (
    <ul className={`list${props.className ? ` ${props.className}` : ""}`} aria-label={props["aria-label"]}>
      {props.children}
    </ul>
  );
}

export function ListRow(props: {
  title: ReactNode;
  /** Second line under the title. */
  subtitle?: ReactNode;
  /** Small tags after the title, e.g. quality or origin. */
  badges?: ReactNode;
  /** Prominent figure on the right, e.g. a price or a value. */
  value?: ReactNode;
  /** Buttons or links on the right; with <see cref="onClick"/> they sit outside the tappable area. */
  actions?: ReactNode;
  /** Icon or marker in front of the title. */
  leading?: ReactNode;
  /** Content under the row, outside the tappable area, e.g. a collapsed description. */
  below?: ReactNode;
  /** Makes the whole row a button, shown with a chevron. */
  onClick?: () => void;
  ariaLabel?: string;
  className?: string;
  muted?: boolean;
  selected?: boolean;
  /** A row inside another list item, e.g. in a tree, is a div instead of a list item. */
  as?: "li" | "div";
}) {
  const classes = ["list-row", props.className, props.below ? "has-below" : "", props.muted ? "muted-row" : "", props.selected ? "selected" : ""].filter(Boolean).join(" ");
  const body = (
    <>
      {props.leading && <span className="list-leading">{props.leading}</span>}
      <span className="list-main">
        <span className="list-title">
          {props.title}
          {props.badges}
        </span>
        {props.subtitle && <span className="list-subtitle">{props.subtitle}</span>}
      </span>
      {props.value != null && <span className="list-value">{props.value}</span>}
      {props.onClick && <span className="list-chevron" aria-hidden="true">›</span>}
    </>
  );
  const Element = props.as ?? "li";
  return (
    <Element className={classes}>
      {props.onClick ? (
        <button type="button" className="list-tap" onClick={props.onClick} aria-label={props.ariaLabel}>
          {body}
        </button>
      ) : (
        <div className="list-tap static">{body}</div>
      )}
      {props.actions && <span className="list-actions">{props.actions}</span>}
      {props.below && <div className="list-below">{props.below}</div>}
    </Element>
  );
}

/** Heading inside a card that groups rows, with an optional count or hint on the right. */
export function ListHeading(props: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="list-heading">
      <h4>{props.children}</h4>
      {props.aside && <span className="muted small">{props.aside}</span>}
    </div>
  );
}

/** A budget of points or money: label, used of total and a bar. */
export function BudgetBar(props: { label: ReactNode; used: number; total: number; usedText?: ReactNode; totalText?: ReactNode; hint?: ReactNode; over?: boolean }) {
  const share = props.total > 0 ? Math.min(1, Math.max(0, props.used / props.total)) : 0;
  const over = props.over ?? props.used > props.total;
  return (
    <div className={`budget${over ? " over" : ""}`}>
      <div className="budget-row">
        <span className="budget-label">{props.label}</span>
        <span className="budget-figure">
          {props.usedText ?? props.used} <span className="muted">von</span> {props.totalText ?? props.total}
        </span>
      </div>
      <div className="budget-track" aria-hidden="true">
        <div className="budget-fill" style={{ width: `${share * 100}%` }} />
      </div>
      {props.hint && <div className="muted small">{props.hint}</div>}
    </div>
  );
}

/** A row of toggle chips, e.g. to filter a list. */
export function Chips<T extends string>(props: { options: { value: T; label: string }[]; value: T | null; onChange: (value: T | null) => void; allLabel?: string; ariaLabel?: string }) {
  return (
    <div className="chips" role="group" aria-label={props.ariaLabel}>
      {props.allLabel && (
        <button type="button" className={`chip${props.value === null ? " selected" : ""}`} aria-pressed={props.value === null} onClick={() => props.onChange(null)}>
          {props.allLabel}
        </button>
      )}
      {props.options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`chip${o.value === props.value ? " selected" : ""}`}
          aria-pressed={o.value === props.value}
          onClick={() => props.onChange(o.value === props.value ? null : o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
